'use client';

import Link from 'next/link';

import Button from '@/components/ui/Button/Button';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltipContent
} from '@/components/ui/chart';

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const data = [
  { year: '1990', growth: 5 },
  { year: '1995', growth: 20 },
  { year: '2000', growth: 35 },
  { year: '2005', growth: 60 },
  { year: '2010', growth: 90 },
  { year: '2015', growth: 120 },
  { year: '2020', growth: 160 },
  { year: '2025', growth: 210 }
];

const config = {
  growth: {
    label: 'Growth Index',
    theme: {
      light: '#4A7F38',
      dark: '#a3f7bf'
    }
  }
} satisfies ChartConfig;

export default function DataStorytellingSection() {
  return (
    <section className='bg-background px-6 py-16 md:py-24'>
      <div className='mx-auto max-w-6xl'>
        <div className='grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-20'>
          {/* LEFT — Copy */}
          <div>
            <p className='text-secondary mb-3 text-[10px] font-black tracking-[0.35em] uppercase'>
              The Power of Visuals
            </p>
            <h2 className='text-foreground text-3xl leading-tight font-black tracking-tight md:text-5xl'>
              Unleash your brand&apos;s potential with data storytelling.
            </h2>
            <p className='text-muted-foreground mt-5 mb-8 text-base leading-relaxed'>
              We transform complex bioeconomy datasets into clear, compelling
              narratives. Our audience doesn&apos;t just read; they analyze,
              understand, and act upon the trends that shape the future of
              biotech.
            </p>

            <Link href='/contact'>
              <Button
                variant='primary'
                className='!px-7 !py-3.5 !text-[12px]'>
                Partner Up
                <ArrowRight className='ml-2 inline-block size-4' />
              </Button>
            </Link>
          </div>

          {/* RIGHT — Chart */}
          <motion.div
            className='rounded-[2.5rem] border border-[#066653] bg-transparent p-6 shadow-sm md:p-10 dark:border-[#a3f7bf]'
            whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}>
            <p className='mb-8 text-[11.5px] font-black tracking-[0.25em] text-[#066653] uppercase dark:text-[#a3f7bf]'>
              Bioinformatics Market Growth Indicator
            </p>
            <div className='text-foreground h-[350px] w-full'>
              <ChartContainer
                config={config}
                className='h-full w-full'>
                <ResponsiveContainer
                  width='100%'
                  height='100%'>
                  <BarChart
                    data={data}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid
                      strokeDasharray='4 4'
                      stroke='hsl(var(--muted-foreground))'
                      opacity={0.4}
                    />
                    <XAxis
                      dataKey='year'
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fill: 'currentColor',
                        fontSize: 13,
                        fontWeight: 500
                      }}
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fill: 'currentColor',
                        fontSize: 13,
                        fontWeight: 500
                      }}
                      dx={-10}
                    />
                    <Tooltip
                      content={(props) => (
                        <ChartTooltipContent
                          {...props}
                          hideIndicator
                        />
                      )}
                      cursor={false}
                    />
                    <Bar
                      dataKey='growth'
                      fill='var(--color-growth)'
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
